import { createTheme } from '@mui/material/styles';
import { enUS as muiEnUS, frFR as muiFrFR, zhCN as muiZhCN, zhTW as muiZhTW, esES as muiEsES } from '@mui/material/locale';
import { enUS as gridEnUS, frFR as gridFrFR, zhCN as gridZhCN, zhTW as gridZhTW, esES as gridEsES } from '@mui/x-data-grid/locales';

// Emerald & Muted Lavender Design Tokens
const EMERALD = '#006D43';
const EMERALD_LIGHT = '#00A86B';
const EMERALD_DARK = '#00331D';
const SLATE = '#515F74';
const SLATE_LIGHT = '#D5E3FC';
const SLATE_DARK = '#1C2025';
const LAVENDER = '#B57EDC';
const ALABASTER = '#F7F9FB';
const WARM_CHARCOAL = '#1A1C1A';

export const themeOptions = {
    palette: {
        primary: {
            main: EMERALD,
            light: EMERALD_LIGHT,
            dark: EMERALD_DARK,
            contrastText: '#ffffff',
        },
        secondary: {
            main: SLATE,
            light: SLATE_LIGHT,
            dark: SLATE_DARK,
            contrastText: '#ffffff',
        },
        info: {
            main: LAVENDER,
            light: '#EBD3FF',
            dark: '#8F59B5',
            contrastText: '#ffffff',
        },
        background: {
            default: ALABASTER,
            paper: '#FFFFFF',
        },
        text: {
            primary: WARM_CHARCOAL,
            secondary: SLATE,
        },
        success: {
            main: EMERALD,
            contrastText: '#ffffff',
        },
        error: {
            main: '#BA1A1A',
        },
        chartColors: [
            EMERALD,
            '#2563EB',
            '#7C3AED',
            '#D97706',
            '#E11D48',
            '#0891B2',
            '#C026D3',
            '#EA580C',
            '#0D9488',
            '#64748B'
        ],
    },
    typography: {
        fontFamily: '"Work Sans", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        h1: { fontFamily: '"Manrope", sans-serif', fontWeight: 800, letterSpacing: '-0.02em' },
        h2: { fontFamily: '"Manrope", sans-serif', fontWeight: 700, letterSpacing: '-0.01em' },
        h3: { fontFamily: '"Manrope", sans-serif', fontWeight: 700 },
        h4: { fontFamily: '"Manrope", sans-serif', fontWeight: 600, letterSpacing: '-0.02em', color: WARM_CHARCOAL },
        h5: { fontFamily: '"Manrope", sans-serif', fontWeight: 600, color: WARM_CHARCOAL },
        h6: { fontFamily: '"Manrope", sans-serif', fontWeight: 600 },
        subtitle1: { fontWeight: 500 },
        overline: { 
            fontFamily: '"Manrope", sans-serif', 
            fontWeight: 800, 
            fontSize: '0.75rem', 
            letterSpacing: '0.15em', 
            textTransform: 'uppercase',
            color: EMERALD,
        },
        button: { textTransform: 'none', fontWeight: 500, letterSpacing: '0.02em' },
    },
    shape: {
        borderRadius: 6,
        inputRadius: 12,
        containerRadius: 32,
        inputHeight: 56,
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    boxShadow: 'none',
                    padding: '8px 16px',
                    '&.Mui-disabled': {
                        backgroundColor: '#E5E5E5',
                        color: '#A0A0A0',
                    }
                },
                containedPrimary: {
                    backgroundColor: EMERALD,
                    color: '#ffffff',
                    '&:hover': {
                        backgroundColor: EMERALD_DARK,
                        boxShadow: '0px 12px 24px -4px rgba(181, 126, 220, 0.08)', 
                    },
                    '&.Mui-disabled': {
                        backgroundColor: '#E5E5E5',
                        color: '#A0A0A0',
                    }
                },
                containedSecondary: {
                    backgroundColor: SLATE_LIGHT,
                    color: SLATE,
                    boxShadow: 'none',
                    '&:hover': {
                        backgroundColor: '#C8D6E8',
                        boxShadow: 'none',
                    },
                }
            },
            variants: [
                {
                    props: { variant: 'filter' },
                    style: {
                        minWidth: 54,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        fontSize: '0.725rem',
                        letterSpacing: '0.04em',
                        padding: '6px 16px',
                        borderRadius: 12,
                        border: '1px solid rgba(81, 95, 116, 0.2)',
                        color: SLATE,
                        backgroundColor: 'transparent',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            backgroundColor: 'rgba(81, 95, 116, 0.05)',
                            borderColor: EMERALD,
                            color: WARM_CHARCOAL,
                        },
                    }
                },
                {
                    props: { variant: 'filter', color: 'primary' },
                    style: {
                        backgroundColor: EMERALD,
                        color: '#ffffff',
                        borderColor: EMERALD,
                        '&:hover': {
                            backgroundColor: EMERALD_DARK,
                            borderColor: EMERALD_DARK,
                            color: '#ffffff',
                        }
                    }
                }
            ]
        },
        MuiCssBaseline: {
            styleOverrides: {
                'input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                },
                'input[type=number]': {
                    MozAppearance: 'textfield',
                    appearance: 'textfield',
                },
                select: {
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23006D43' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 20px center',
                    backgroundSize: '18px 18px',
                    paddingRight: '48px !important',
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                select: {
                    paddingTop: 8,
                    paddingBottom: 8,
                    paddingRight: '48px !important',
                    fontWeight: 600,
                },
                icon: {
                    right: 20,
                    fontSize: '1.5rem',
                    color: EMERALD,
                },
            },
        },
        MuiNativeSelect: {
            styleOverrides: {
                select: {
                    paddingRight: '48px !important',
                },
                icon: {
                    right: 20,
                    fontSize: '1.5rem',
                    color: EMERALD,
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    overflow: 'hidden',
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(81, 95, 116, 0.2)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: EMERALD,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: EMERALD,
                        borderWidth: '1px',
                    },
                    '& .MuiSelect-select': {
                        paddingRight: '48px !important',
                    },
                    '& .MuiSelect-icon': {
                        right: '20px',
                        fontSize: '1.5rem',
                        color: EMERALD,
                    },
                },
                input: {
                    padding: '8px 14px',
                    borderRadius: 'inherit',
                }
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    color: SLATE,
                    fontWeight: 600,
                    '&.Mui-focused': {
                        color: EMERALD,
                    },
                },
                shrink: {
                    transform: 'translate(14px, -6px) scale(0.75)',
                }
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    backgroundImage: 'none', 
                    border: '1px solid rgba(81, 95, 116, 0.15)',
                },
                elevation1: {
                    boxShadow: '0px 12px 32px rgba(181, 126, 220, 0.04)', 
                },
                elevation2: {
                    boxShadow: '0px 16px 40px rgba(181, 126, 220, 0.06)',
                },
                elevation3: {
                    boxShadow: '0px 24px 48px rgba(181, 126, 220, 0.08)',
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: 'rgba(247, 249, 251, 0.8)', 
                    backdropFilter: 'blur(20px)',
                    color: WARM_CHARCOAL,
                    boxShadow: 'none',
                    borderRadius: 0,
                    border: 'none',
                    borderBottom: '1px solid rgba(81, 95, 116, 0.1)', 
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    border: '1px solid rgba(81, 95, 116, 0.15)',
                    transition: 'all 0.2s ease-in-out',
                    boxShadow: '0px 12px 32px rgba(181, 126, 220, 0.04)', 
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderBottom: '1px solid rgba(81, 95, 116, 0.1)',
                    padding: '16px',
                },
                head: {
                    color: SLATE,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    letterSpacing: '0.05em',
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 12,
                        '& fieldset': {
                            borderColor: EMERALD,
                            opacity: 0.8,
                        },
                        '&:hover fieldset': {
                            borderColor: EMERALD,
                            opacity: 1,
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: EMERALD,
                            borderWidth: '2px',
                        },
                    },
                }
            }
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: 32,
                    border: '1px solid rgba(81, 95, 116, 0.15)',
                    boxShadow: '0px 24px 48px rgba(181, 126, 220, 0.1)',
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    letterSpacing: '0.02em',
                },
                outlined: {
                    borderColor: 'rgba(81, 95, 116, 0.3)',
                    backgroundColor: 'transparent',
                    '&:hover': {
                        backgroundColor: 'rgba(81, 95, 116, 0.05)',
                    }
                },
                filled: {
                    border: '1px solid transparent',
                }
            }
        },
        MuiFilledInput: {
            styleOverrides: {
                root: {
                    backgroundColor: 'rgba(0, 109, 67, 0.05)',
                    borderRadius: 12,
                    overflow: 'hidden',
                    height: 56,
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        backgroundColor: 'rgba(0, 109, 67, 0.08)',
                    },
                    '&.Mui-focused': {
                        backgroundColor: '#FFFFFF',
                        boxShadow: `0 0 0 2px ${EMERALD}`,
                    },
                    '&:before, &:after': {
                        display: 'none',
                    },
                    display: 'flex',
                    alignItems: 'center',
                    '& .MuiInputAdornment-root': {
                        height: '100%',
                        marginTop: '0 !important',
                        display: 'flex',
                        alignItems: 'center',
                    },
                    '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                        paddingRight: '48px !important',
                        height: '100%',
                        boxSizing: 'border-box',
                    },
                    '& .MuiSelect-icon': {
                        right: '20px',
                        fontSize: '1.5rem',
                        color: EMERALD,
                    },
                },
                input: {
                    paddingTop: 0,
                    paddingBottom: 0,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    borderRadius: 'inherit',
                },
            },
        },
        MuiToggleButtonGroup: {
            styleOverrides: {
                root: {
                    height: 56,
                    backgroundColor: 'rgba(0, 109, 67, 0.05)',
                    borderRadius: 12,
                    padding: '4px',
                    border: 'none',
                    gap: '4px',
                    '& .MuiToggleButtonGroup-grouped': {
                        border: 'none',
                        borderRadius: '8px !important',
                        margin: 0,
                    },
                },
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    border: 'none',
                    textTransform: 'none',
                    fontWeight: 600,
                    height: '100%',
                    color: 'rgba(0, 109, 67, 0.5)',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        backgroundColor: 'rgba(0, 109, 67, 0.03)',
                        color: WARM_CHARCOAL,
                    },
                    '&.Mui-selected': {
                        boxShadow: '0 2px 8px rgba(181, 126, 220, 0.08)',
                    },
                },
                standard: {
                    '&.Mui-selected': {
                        backgroundColor: '#FFFFFF !important',
                        color: `${EMERALD} !important`,
                        '&:hover': {
                            backgroundColor: '#FFFFFF !important',
                            color: `${WARM_CHARCOAL} !important`,
                        },
                    },
                },
                primary: {
                    '&.Mui-selected': {
                        backgroundColor: `${EMERALD} !important`,
                        color: '#FFFFFF !important',
                        '&:hover': {
                            backgroundColor: `${EMERALD_DARK} !important`,
                            color: '#FFFFFF !important',
                        },
                    },
                },
                success: {
                    '&.Mui-selected': {
                        backgroundColor: `${EMERALD} !important`,
                        color: '#FFFFFF !important',
                        '&:hover': {
                            backgroundColor: `${EMERALD_DARK} !important`,
                            color: '#FFFFFF !important',
                        },
                    },
                },
                error: {
                    '&.Mui-selected': {
                        backgroundColor: '#BA1A1A !important',
                        color: '#FFFFFF !important',
                        '&:hover': {
                            backgroundColor: '#93000A !important',
                            color: '#FFFFFF !important',
                        },
                    },
                },
            },
        },
    },
};

export const muiLocaleMap = {
    'en': [muiEnUS, gridEnUS],
    'fr': [muiFrFR, gridFrFR],
    'zh-CN': [muiZhCN, gridZhCN],
    'zh-TW': [muiZhTW, gridZhTW],
    'es': [muiEsES, gridEsES]
};

export const getThemeWithLocale = (language = 'en') => {
    const [muiLocale, gridLocale] = muiLocaleMap[language] || muiLocaleMap['en'];
    return createTheme(themeOptions, muiLocale, gridLocale);
};

const theme = getThemeWithLocale('en');

export default theme;
